import type { StudentIdentitySummary } from '@/components/students/StudentIdentity';
import { ApiError, fetchApi } from '@/services/api-client';

import type { OnlineTestStatus } from './class-tests';

export type OnlineQuestionType = 'single' | 'multiple' | 'order' | 'subtraction' | 'text';

export const ONLINE_QUESTION_TYPES: OnlineQuestionType[] = ['single', 'multiple', 'order', 'subtraction', 'text'];

export interface OnlineOption {
  id: string;
  text: string;
  isCorrect: boolean;
  isWrong: boolean;
}

export interface OnlineQuestion {
  id: string;
  type: OnlineQuestionType;
  text: string;
  points: number;
  extraCredit: boolean;
  removed: boolean;
  modelAnswer: string | null;
  /** For an order question, the correct order. */
  options: OnlineOption[];
}

export interface OnlineSection {
  id: string;
  name: string;
  questions: OnlineQuestion[];
}

/** The teacher's copy, answer key included. `locked` once any student has started. */
export interface OnlineDefinition {
  locked: boolean;
  sections: OnlineSection[];
  max: number;
  extraMax: number;
}

/** An item without an `id` is new. */
export interface SaveOnlineQuestionsBody {
  sections: Array<{
    id?: string;
    name: string;
    questions: Array<{
      id?: string;
      type: OnlineQuestionType;
      text: string;
      points: number;
      extraCredit: boolean;
      removed?: boolean;
      modelAnswer?: string | null;
      options: Array<{ id?: string; text: string; isCorrect: boolean; isWrong: boolean }>;
    }>;
  }>;
}

/** The edits allowed after the lock; for an order question, listing every option sets the correct order. */
export interface PatchOnlineQuestionBody {
  text?: string;
  points?: number;
  removed?: boolean;
  modelAnswer?: string | null;
  options?: Array<{ id: string; text?: string; isCorrect?: boolean; isWrong?: boolean }>;
}

export type OnlineImportMode = 'append' | 'replace';

export interface OnlineAiPrompt {
  prompt: string;
  exampleCsv: string;
}

/** A problem in the file itself (`row` is the spreadsheet row, header = 1) or in a question it describes. */
export type OnlineImportError =
  | { row: number; column: string | null; message: string }
  | { section: number; question: number | null; message: string };

/** The per-row problems behind a refused import or save, or null when the failure was something else. */
export function onlineImportErrorsOf(error: unknown): OnlineImportError[] | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const errors = (error.body as { errors?: unknown } | undefined)?.errors;
  return Array.isArray(errors) ? (errors as OnlineImportError[]) : null;
}

export type OnlineLiveState = 'not_started' | 'in_progress' | 'submitted';

export type OnlineEventType = 'left' | 'returned' | 'fullscreen_exit';

export interface OnlineEvent {
  type: OnlineEventType;
  at: string;
}

export interface OnlineLiveStudent {
  student: StudentIdentitySummary;
  state: OnlineLiveState;
  answered: number;
  total: number;
  leftCount: number;
  events: OnlineEvent[];
}

export interface OnlineLiveView {
  opened: boolean;
  closed: boolean;
  students: OnlineLiveStudent[];
}

export interface OnlineTextAnswer {
  attemptId: string;
  student: StudentIdentitySummary;
  text: string;
  teacherPoints: number | null;
}

export interface OnlineTextQuestion {
  id: string;
  text: string;
  points: number;
  modelAnswer: string | null;
  answers: OnlineTextAnswer[];
}

export interface OnlineTextAnswers {
  questions: OnlineTextQuestion[];
}

export interface OnlineTextMark {
  attemptId: string;
  questionId: string;
  points: number;
}

/** What the student gave, by question type. */
export type OnlineAnswer = { optionId: string } | { optionIds: string[] } | { order: string[] } | { text: string };

export interface OnlineReviewQuestion {
  id: string;
  type: OnlineQuestionType;
  text: string;
  points: number;
  /** In the student's order. */
  options: OnlineOption[];
  answer: OnlineAnswer | null;
  autoPoints: number | null;
  /** The mark of a free-text answer, or an override of an auto mark. */
  teacherPoints: number | null;
  overridden: boolean;
  explanation: string;
  removed: boolean;
}

export interface OnlineAttemptReview {
  student: StudentIdentitySummary;
  /** Marks can be changed only once the attempt is submitted. */
  submitted: boolean;
  sections: Array<{ name: string; questions: OnlineReviewQuestion[] }>;
  events: OnlineEvent[];
}

/** `points: null` clears the override, or a free-text mark. */
export interface OnlineOverride {
  questionId: string;
  points: number | null;
}

const online = (id: string) => `/admin/class-tests/${id}/online`;

export const onlineTestsApi = {
  questions: (id: string) => fetchApi<OnlineDefinition>(`${online(id)}/questions`),
  saveQuestions: (id: string, body: SaveOnlineQuestionsBody) =>
    fetchApi<OnlineDefinition>(`${online(id)}/questions`, { method: 'PUT', body: JSON.stringify(body) }),
  patchQuestion: (id: string, questionId: string, body: PatchOnlineQuestionBody) =>
    fetchApi<OnlineDefinition>(`${online(id)}/questions/${questionId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  importCsv: (id: string, csv: string, mode: OnlineImportMode) =>
    fetchApi<OnlineDefinition>(`${online(id)}/import`, { method: 'POST', body: JSON.stringify({ csv, mode }) }),
  aiPrompt: (id: string) => fetchApi<OnlineAiPrompt>(`${online(id)}/ai-prompt`),
  open: (id: string) => fetchApi<OnlineTestStatus>(`${online(id)}/open`, { method: 'POST' }),
  close: (id: string) => fetchApi<OnlineTestStatus>(`${online(id)}/close`, { method: 'POST' }),
  reopen: (id: string) => fetchApi<OnlineTestStatus>(`${online(id)}/reopen`, { method: 'POST' }),
  reopenStudent: (id: string, studentId: string) =>
    fetchApi<{ ok: true }>(`${online(id)}/students/${studentId}/reopen`, { method: 'POST' }),
  live: (id: string) => fetchApi<OnlineLiveView>(`${online(id)}/live`),
  textAnswers: (id: string) => fetchApi<OnlineTextAnswers>(`${online(id)}/text-answers`),
  saveTextMarks: (id: string, marks: OnlineTextMark[]) =>
    fetchApi<OnlineTextAnswers>(`${online(id)}/text-answers`, { method: 'PUT', body: JSON.stringify({ marks }) }),
  review: (id: string, studentId: string) => fetchApi<OnlineAttemptReview>(`${online(id)}/attempts/${studentId}`),
  saveOverrides: (id: string, studentId: string, items: OnlineOverride[]) =>
    fetchApi<OnlineAttemptReview>(`${online(id)}/attempts/${studentId}/overrides`, {
      method: 'PUT',
      body: JSON.stringify(items),
    }),
};
